import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/layout/Header";
import Footer from "@/components/landing/layout/Footer";
import { ShieldCheck, ChevronDown, Heart } from "lucide-react";
import Lenis from "lenis";
import { useGSAP } from "@/hooks/useGSAP";

type FaqItem = {
  q: string;
  a: string;
};

const FAQS: FaqItem[] = [
  {
    q: "What is protected?",
    a: "Protega covers your car accessories from life’s unexpected moments—scratches, tears, wear, and more. From the day you buy your product, you’re protected for issues typical warranties won’t cover.",
  },
  {
    q: "What happens if I return my product?",
    a: "If you return your product, Autofurnish will refund the amount you paid for product protection. Depending on the time since purchase, you may receive a prorated refund (less any paid service).",
  },
  {
    q: "I haven’t received a refund for my protection even though I returned my product.",
    a: "Autofurnish handles all transactions for product protection sales and refunds. For assistance with refunds, contact our Customer Care.",
  },
  {
    q: "Is this plan the same as warranty or insurance?",
    a: "No. It is an optional protection plan for accidental events, separate from any manufacturer warranty or insurance.",
  },
];

interface CardAccident {
  image: string;
  Heading: string;
  para: string;
}

const cardAccident: CardAccident[] = [
  {
    image: "/clients/AutoFurnish/Accident1.png",
    Heading: "Covered for Tears",
    para: "Accidental rips or snags are protected.",
  },
  {
    image: "/clients/AutoFurnish/Accident2.png",
    Heading: "Protected from Pet Damage",
    para: "Scratches or bites won’t leave you uncovered.",
  },
  {
    image: "/clients/AutoFurnish/Accident3.png",
    Heading: "Safe from Drops Accidents",
    para: "Food, drink, or accidental spills are covered.",
  },
];

const AccordionItem: React.FC<{
  item: FaqItem;
  index: number;
  isOpen: boolean;
  onToggle: (i: number) => void;
}> = ({ item, index, isOpen, onToggle }) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [maxH, setMaxH] = useState<string>("0px");

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (isOpen) {
      setMaxH(`${el.scrollHeight}px`);
      const t = setTimeout(() => setMaxH("none"), 300);
      return () => clearTimeout(t);
    } else {
      if (el.scrollHeight && maxH === "none") {
        setMaxH(`${el.scrollHeight}px`);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setMaxH("0px"));
        });
      } else {
        setMaxH("0px");
      }
    }
  }, [isOpen]);

  return (
    <div className="tw-border-b tw-text-left tw-px-6 tw-border-gray-200">
      <button
        aria-expanded={isOpen}
        aria-controls={`faq-${index}`}
        onClick={() => onToggle(index)}
        className="tw-w-full tw-text-left tw-py-4 tw-flex tw-justify-between tw-items-start tw-gap-4 "
      >
        <span className="tw-text-lg tw-font-medium">{item.q}</span>
        <ChevronDown
          className={`tw-h-5 tw-w-5 tw-transition-transform tw-duration-300 ${
            isOpen ? "tw-rotate-180" : "tw-rotate-0"
          }`}
        />
      </button>

      <div
        id={`faq-${index}`}
        ref={contentRef}
        style={{
          maxHeight: maxH === "none" ? undefined : maxH,
          transition: "max-height 300ms ease, opacity 200ms ease",
          opacity: isOpen ? 1 : 0,
          overflow: "hidden",
        }}
        className="tw-pb-4 tw-pr-6 tw-text-sm tw-text-gray-700"
        aria-hidden={!isOpen}
      >
        <div className="tw-pt-1">{item.a}</div>
      </div>
    </div>
  );
};
interface CardProtection {
  icon: React.ElementType;
  Heading: string;
  para: string;
}
const cardProtection: CardProtection[] = [
  {
    icon: ShieldCheck,
    Heading: "Accidental damage",
    para: "File a claim with Protega’s 24/7 online support. Most claims are resolved within minutes.",
  },
  {
    icon: Heart,
    Heading: "Hassle-Free Claims",
    para: "Protect your order from loss, damage, or theft during transit. If your package doesn’t arrive or arrives damaged, we’ll make it right.",
  },
];

const AutoFurnishCare: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleIndex = (i: number) => {
    setOpenIndex((prev) => (prev === i ? null : i));
  };

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-bg-black tw-text-white tw-font-mori">
      <Header />
      <main className="tw-relative">
        <div className="tw-h-20 lg:tw-h-24" />

        {/* HERO */}
        <section className="tw-relative tw-h-[85vh] tw-overflow-hidden">
          <img
            src="/clients/AutoFurnish/Hero.png"
            alt="Hero"
            className="tw-absolute tw-inset-0 tw-object-cover tw-w-full tw-h-full"
          />
          <div className="tw-relative tw-z-10 tw-flex tw-items-center tw-justify-start md:tw-px-24 tw-h-full">
            <div className="tw-mt-auto tw-mx-auto tw-mb-20 lg:tw-w-[75%] tw-px-10  tw-relative tw-z-10">
              <div className="lg:tw-mb-28 tw-mb-10">
                <img
                  src="/icons/logo.png"
                  className="tw-max-h-5 lg:tw-ml-2"
                  alt="Logo"
                />
                <h1 className="lg:tw-text-7xl md:tw-text-6xl tw-text-4xl tw-text-black">
                  {" "}
                  Enjoy Peace of Mind
                </h1>
              </div>
              <div className="md:tw-w-2/3 tw-flex-col tw-gap-5 tw-font-extralight tw-flex tw-items-start tw-text-black">
                <span className="md:tw-text-3xl tw-text-xl">
                  Simple, hassle-free protection for the auto accessories you
                  rely on.
                </span>
                <a
                  href="https://www.autofurnish.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:tw-bg-black hover:tw-text-white tw-transition-all tw-px-6 tw-text-xl tw-py-2 tw-border-2 tw-border-black"
                >
                  Shop Now
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Logos */}
        <section className="tw-bg-white tw-py-20">
          <div className="tw-flex tw-items-center tw-justify-center">
            <img
              src="/clients/AutoFurnish/logo.png"
              className="tw-max-w-44 tw-py-4 tw-px-6"
              alt="Auto Furnish Logo"
            />
            <img
              src="/icons/logo.png"
              className="tw-border-l-2 tw-border-black tw-py-4 tw-px-6 tw-max-w-44"
              alt="Protega Logo"
            />
          </div>
        </section>

        {/* Accidental Damage */}
        <section className="tw-py-20 tw-flex tw-flex-col tw-gap-20 tw-items-center tw-justify-center tw-bg-[#2B2B2B] tw-text-white">
          <h1 className="md:tw-text-4xl tw-text-2xl tw-font-medium">
            Accidental Damage Protection
          </h1>
          <div className="tw-flex tw-flex-wrap tw-w-full tw-gap-10 tw-justify-center">
            {cardAccident?.map((item, count) => (
              <div
                key={count}
                className="tw-max-w-[280px] tw-gap-2 tw-text-center tw-flex tw-flex-col tw-items-center"
              >
                <img
                  src={item.image}
                  alt={item.Heading}
                  className="tw-w-4/6 tw-h-40 tw-object-cover"
                />
                <h2 className="tw-text-lg tw-font-thin">{item.Heading}</h2>
                <span className="tw-text-sm">{item.para}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Protection plan coverage */}
        <section className="tw-bg-white tw-text-black">
          <div className="tw-py-32 tw-flex tw-gap-10 tw-flex-col tw-w-[95%] tw-mx-auto tw-items-center tw-justify-center">
            <h1 className="tw-text-3xl">Protection plan coverage*</h1>
            <div className="tw-flex tw-flex-wrap tw-gap-10 tw-w-10/12 tw-justify-center">
              {cardProtection.map((item, index) => (
                <div
                  key={index}
                  className="tw-flex tw-items-start tw-max-w-80 tw-gap-4"
                >
                  <item.icon
                    strokeWidth={1}
                    className="tw-h-28 tw-w-28 tw-self-start "
                  />
                  <div>
                    <h3 className="tw-font-semibold ">{item.Heading}</h3>
                    <p className="tw-text-gray-600 tw-text-sm">{item.para}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="tw-relative">
          <img
            src="/clients/AutoFurnish/Banner.png"
            className="tw-w-full tw-max-h-[90vh] tw-object-cover tw-object-center"
            alt=""
          />
        </section>

        {/* Simple Coverage */}
        <section className="tw-py-20 tw-text-center tw-bg-white tw-text-black">
          <h2 className="tw-text-2xl md:tw-text-3xl tw-font-semibold tw-mb-6">
            Protection made simple, coverage made strong
          </h2>
          <p className="tw-text-gray-700">
            ✓ Accidental Damage Coverage: No sweat repairs or replacements in
            case of accidental damage depending on your product.
          </p>
        </section>

        <section className="tw-bg-white tw-pb-20">
          <hr className="tw-w-[85%] tw-border-gray-600 tw-mx-auto" />
          <div className="tw-py-28 tw-mx-auto tw-w-[95%] tw-text-center tw-text-black">
            <h1 className="tw-text-4xl tw-mb-10 tw-font-semibold">
              Why add Protega?
            </h1>
            <p className="tw-w-10/12 tw-text-lg tw-mx-auto md:tw-text-justify">
              Protega provides coverage for your automotive products against
              unexpected accidents—whether it’s scratches, wear from daily use,
              or unexpected damage during handling. From seat covers to car
              mats, accidental damage coverage has you protected.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="tw-py-10 tw-relative tw-bg-[#EDEDED] tw-min-h-[90vh] tw-overflow-hidden tw-text-black">
          <img
            src="/LovePangolinFAQ/HowItWorks.png"
            className="tw-absolute lg:tw-h-[90%] lg:tw-top-5 lg:tw-right-20 -tw-right-10 lg:tw-w-8/12 tw-object-top tw-object-cover tw-hidden md:tw-block"
            alt="Image"
          />
          <div className="md:tw-w-4/5 tw-mx-auto tw-relative tw-z-10">
            <h1 className="tw-text-5xl tw-text-center tw-mb-10 tw-font-semibold">
              How it works
            </h1>
            <div className="md:tw-w-2/5 md:tw-px-0 tw-px-10 tw-flex tw-flex-col tw-gap-5 tw-items-start">
              <h2 className="tw-text-3xl tw-font-semibold">
                Easy claims, great service
              </h2>
              <p className="tw-text-lg">
                Filing a claim is simple. They’ll process your claim in minutes
                and once approved, they’ll mail you details on your replacement.{" "}
              </p>
              <Link
                to={"/customer/claims"}
                className="tw-border hover:tw-bg-black hover:tw-text-white tw-transition-all tw-border-black tw-px-6 tw-py-3"
              >
                Start a Claim
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="tw-bg-white tw-text-black tw-py-20">
          <div className="tw-w-[95%] tw-mx-auto  tw-text-center">
            <h2 className="tw-text-3xl tw-font-semibold tw-mb-10">
              Auto Furnish Care Plan FAQs
            </h2>
            <div className="tw-max-w-4xl tw-mx-auto tw-mb-20 tw-shadow-sm tw-border tw-border-gray-100 tw-rounded">
              {FAQS.map((item, idx) => (
                <AccordionItem
                  key={idx}
                  item={item}
                  index={idx}
                  isOpen={openIndex === idx}
                  onToggle={toggleIndex}
                />
              ))}
            </div>
            <Link
              to={"/auto-furnish-productprotection/tnc"}
              className="hover:tw-underline tw-font-medium hover:tw-text-blue-700"
            >
              Terms and Conditions
            </Link>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default AutoFurnishCare;
